import React, { useState } from 'react';
import Button from '../../../components/UI/Button';
import Icon from '../../../components/AppIcon';
import { ResumeFormat } from '../types';

interface ResumeDownloadSectionProps {
  resumeFormats: ResumeFormat[];
}

const ResumeDownloadSection = ({ resumeFormats }: ResumeDownloadSectionProps) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (format: ResumeFormat) => {
    setDownloadingId(format.id);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const link = document.createElement('a');
      link.href = format.url;
      link.download = `resume.${format.format.toLowerCase()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'PDF': return 'FileText';
      case 'Word': return 'FileType';
      case 'Online': return 'Globe';
      default: return 'Download';
    }
  };

  return (
    <section className="bg-white border-2 border-[#0C2C39] shadow-[4px_4px_0px_0px_#0C2C39] rounded-[2rem] p-8 sm:p-12 mb-12 relative overflow-hidden">
      {/* Subtle Pattern Grid */}
      <div className="absolute inset-0 z-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#0C2C39 0.5px, transparent 0.5px)',
          backgroundSize: '24px 24px'
        }} />

      <div className="relative z-10 mb-12">
        <h2 className="text-4xl sm:text-5xl font-black text-[#0C2C39] mb-4 tracking-tighter font-roboto">
          Resume <br />
          <span className="text-teal-700/80 italic">Repository</span>
        </h2>
        <p className="text-[#0C2C39]/50 text-lg font-medium">Download my technical resume in your preferred format</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
        {resumeFormats.map((format) => (
          <div
            key={format.id}
            className="group neubrutal-card bg-white p-8 flex flex-col items-center text-center transition-all duration-300"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#0C2C39]/5 border-2 border-[#0C2C39] flex items-center justify-center mb-6 shadow-[2px_2px_0px_0px_#0C2C39] group-hover:shadow-none transition-all duration-300 text-[#0C2C39]">
              <Icon name={getFormatIcon(format.format) as any} size={32} />
            </div>

            <h3 className="text-xl font-black text-[#0C2C39] mb-2 tracking-tight">
              {format.name}
            </h3>
            <p className="text-[10px] font-black text-teal-700 uppercase tracking-widest mb-6">
              {format.format} Format • {format.size}
            </p>

            <div className="w-full space-y-4 pt-6 border-t-2 border-[#0C2C39]/5">
              <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-[#0C2C39]/40 mb-2">
                <span>Update Trace</span>
                <span>{format.lastUpdated}</span>
              </div>

              <Button
                onClick={() => handleDownload(format)}
                className={`w-full py-4 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 border-2 border-[#0C2C39] ${downloadingId === format.id
                    ? 'bg-white text-[#0C2C39]'
                    : 'bg-[#0C2C39] text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,0.1)] hover:shadow-none'
                  }`}
              >
                {downloadingId === format.id ? 'Processing...' : `Download ${format.format}`}
              </Button>

              {format.format === 'Online' && (
                <Button
                  className="w-full py-4 text-[10px] font-black uppercase tracking-widest rounded-xl bg-white border-2 border-[#0C2C39] text-[#0C2C39] shadow-[3px_3px_0px_0px_#0C2C39] hover:shadow-none transition-all duration-300"
                  onClick={() => window.open(format.url, '_blank')}
                >
                  View Archive
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Preview Call-to-action */}
      <div className="mt-12 p-8 bg-[#0C2C39]/5 border-2 border-dashed border-[#0C2C39]/10 rounded-2xl relative z-10">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="w-14 h-14 bg-white border-2 border-[#0C2C39]/20 rounded-2xl flex items-center justify-center text-teal-700">
            <Icon name="Eye" size={24} />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h4 className="font-black text-[#0C2C39] text-lg mb-1 tracking-tight">Instant Verification</h4>
            <p className="text-[#0C2C39]/60 text-sm font-medium">
              Want to preview before downloading? Initialize a safe sandbox view of my resume content.
            </p>
          </div>
          <Button
            className="px-8 py-4 bg-white border-2 border-[#0C2C39] text-[#0C2C39] font-black uppercase tracking-widest text-[10px] rounded-xl shadow-[3px_3px_0px_0px_#0C2C39] hover:shadow-none transition-all duration-300"
            onClick={() => alert('Resume preview functionality initializing...')}
          >
            Quick Preview
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ResumeDownloadSection;